"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "@/lib/api/api";

export type ReservationDecision = "accepted" | "rejected";

type RespondVariables = {
  /** One or more schedule ids to moderate (bulk approve/reject). */
  scheduleIds: string[];
  decision: ReservationDecision;
};

/**
 * Approve or reject room reservations awaiting approval via
 * `PUT /schedules/{id}/respond/{status}` (whole-schedule, so no slot id).
 * Handles bulk moderation and refreshes the pending list + sidebar count.
 */
export function useRespondReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ scheduleIds, decision }: RespondVariables) => {
      const uniqueIds = [...new Set(scheduleIds)];
      return Promise.all(
        uniqueIds.map((id) => api.client.schedules.respond(id, null, decision))
      );
    },
    onSuccess: async (_result, { decision, scheduleIds }) => {
      await queryClient.invalidateQueries({
        queryKey: ["pendingReservations"]
      });
      await queryClient.invalidateQueries({ queryKey: ["schedules"] });
      const count = new Set(scheduleIds).size;
      const noun = count === 1 ? "Reservation" : `${count} reservations`;
      toast.success(
        decision === "accepted" ? `${noun} approved` : `${noun} denied`
      );
    },
    onError: () => {
      toast.error("Could not update the reservation. Please try again.");
    }
  });
}
